<!-- Modal Backdrop -->
<div class="fixed inset-0 z-50 flex items-center justify-center bg-black/50" (click)="close()">
  <div
    class="bg-base-100 rounded-lg shadow-xl w-full max-w-4xl m-4 max-h-[90vh] flex flex-col"
    (click)="$event.stopPropagation()"
  >
    <!-- Header -->
    <div class="flex items-center justify-between p-4 border-b border-base-300">
      <div>
        <h3 class="text-lg font-bold">Offene Positionen klären</h3>
        <p class="text-sm text-base-content/60">
          Wareneingang vom {{ receipt.received_at | date: 'dd.MM.yyyy HH:mm' }} ·
          {{ receipt.article_template_description }}
        </p>
      </div>
      <div class="flex items-center gap-3">
        @if (isRecordOnly()) {
          <span class="bm-badge bm-badge-neutral">Erledigt</span>
        } @else {
          <span class="bm-badge bm-badge-neutral">Schritt {{ step() }} von 3</span>
        }
        <button class="bm-btn bm-btn-sm bm-btn-ghost bm-btn-square" (click)="close()">
          <i class="fa fa-times"></i>
        </button>
      </div>
    </div>

    <div class="p-4 overflow-y-auto space-y-5">
      @if (errorMessage()) {
        <div class="bm-alert bm-alert-error bm-alert-soft">{{ errorMessage() }}</div>
      }

      @if (isLoading()) {
        <p class="text-sm text-base-content/60">Wird geladen …</p>
      } @else if (clarifications().length === 0) {
        <p class="text-sm text-base-content/60">Für diese Buchung ist nichts offen.</p>
      } @else {

        <!-- ==================== Schritt 1: Stimmen die Zahlen? ==================== -->
        @if (step() === 1 || isRecordOnly()) {
          <section class="bm-card bm-card-sm bm-card-border">
            <div class="bm-card-body">
              @if (isRecordOnly()) {
                <h4 class="bm-card-title text-base">Diese Buchung ist geklärt</h4>
                <p class="text-sm text-base-content/70">
                  Alle offenen Positionen sind entschieden. Was festgehalten wurde, steht unten.
                </p>
              } @else {
                <h4 class="bm-card-title text-base">1 · Stimmen die Zahlen?</h4>
                <p class="text-sm text-base-content/70">
                  Bevor entschieden wird: Ist die erwartete und die gezählte Menge richtig erfasst?
                  Änderungen werden erst am Ende gespeichert.
                </p>
              }

              <div class="flex flex-wrap gap-6 items-end mt-2">
                <div>
                  <label class="bm-fieldset-label">Erwartet</label>
                  <input
                    type="number"
                    min="0"
                    class="bm-input w-28"
                    [disabled]="isRecordOnly()"
                    [ngModel]="correctedOrdered()"
                    (ngModelChange)="correctedOrdered.set($event)"
                  />
                </div>
                <div>
                  <label class="bm-fieldset-label">Vom Lager gezählt</label>
                  <input
                    type="number"
                    min="1"
                    class="bm-input w-28"
                    [disabled]="!countedQuantityEditable() || isRecordOnly()"
                    [ngModel]="correctedCounted()"
                    (ngModelChange)="correctedCounted.set($event)"
                  />
                </div>
                <div>
                  <span class="bm-fieldset-label">Zugeordnet</span>
                  <p class="text-xl font-bold text-success">{{ transferredCount() }}</p>
                </div>
                <div>
                  <span class="bm-fieldset-label">Offen</span>
                  <p class="text-xl font-bold text-error">{{ totalOpen() }}</p>
                </div>
              </div>

              @if (!countedQuantityEditable()) {
                <p class="text-xs text-base-content/60 mt-2">
                  Bei Positionen mit Seriennummer hängt die Menge an konkreten Nummern und lässt
                  sich hier nicht ändern. Die einzelnen Nummern werden in den nächsten Schritten
                  entschieden.
                </p>
              }
            </div>
          </section>

          <!-- Bereits entschieden -->
          @if (resolvedClarifications().length > 0) {
            <section>
              <h4 class="font-semibold text-sm mb-2">Bereits entschieden</h4>
              <div class="overflow-x-auto">
                <table class="bm-table bm-table-xs">
                  <thead>
                    <tr>
                      <th>Position</th>
                      <th>Entscheidung</th>
                      <th>Menge</th>
                      <th>Artikel</th>
                      <th>Von</th>
                      <th>Wann</th>
                      <th>Notiz</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (clarification of resolvedClarifications(); track clarification.id) {
                      @for (entry of clarification.actions; track entry.id) {
                        <tr>
                          <td>{{ clarification.serial_number || clarification.article_template_description }}</td>
                          <td>{{ actionLabel(entry.action) }}</td>
                          <td>{{ entry.quantity }}x</td>
                          <td>
                            @if (entry.source_article_id) {
                              <a
                                class="text-primary underline"
                                [href]="articleUrl(entry.source_article_id)"
                                target="_blank"
                                rel="noopener"
                              >#{{ entry.source_article_id }}</a>
                            } @else {
                              <span class="text-base-content/40">—</span>
                            }
                          </td>
                          <td>{{ entry.decided_by_name || '-' }}</td>
                          <td>{{ entry.created_at | date: 'dd.MM.yyyy HH:mm' }}</td>
                          <td>{{ entry.note || '-' }}</td>
                        </tr>
                      }
                    }
                  </tbody>
                </table>
              </div>
            </section>
          }
        }

        <!-- ==================== Schritt 2: Steckt der Rest im Versand? ==================== -->
        @if (step() === 2 && !isRecordOnly()) {
          <section>
            <h4 class="font-semibold text-base mb-1">2 · Steckt der Rest noch im Versand?</h4>
            @if (clarificationsWithCandidates().length === 0) {
              <div class="bm-alert bm-alert-warning bm-alert-soft mt-2">
                <i class="fa fa-exclamation-triangle"></i>
                <span>
                  Kein Lieferschein gefunden, über den sich etwas nachbuchen ließe. Dieser Schritt
                  lässt sich überspringen — weiter mit Schritt 3.
                </span>
              </div>
            } @else {
              <p class="text-sm text-base-content/70 mb-2">
                <strong>Häufigste Ursache:</strong> Die Artikel wurden dem Techniker geschickt und
                der Lieferschein wurde quittiert, im System stehen sie aber weiterhin als unterwegs
                — für die Umbuchung sind sie damit unsichtbar. Werden sie hier nachgebucht, entsteht
                kein zweiter Eintrag.
              </p>
              <p class="text-sm text-base-content/70 mb-4">
                Gelistet wird alles, was ins Quell-Lager quittiert wurde. Die Spalte
                <strong>Eingang</strong> nennt je Artikel den Lieferschein, über den <em>dieser</em>
                Artikel dorthin kam — Nicht
                wählbar ist nur, was nachweislich an Dritte unterwegs ist.
              </p>
            }
          </section>

          @for (clarification of clarificationsWithCandidates(); track clarification.id) {
            <section class="bm-card bm-card-sm bm-card-border">
              <div class="bm-card-body">
                <div class="flex items-start justify-between gap-3">
                  <div>
                    <h5 class="bm-card-title text-base">
                      @if (clarification.serial_number) {
                        Seriennummer {{ clarification.serial_number }}
                      } @else {
                        {{ clarification.article_template_description || 'Ohne Artikelvorlage' }}
                      }
                    </h5>
                    @if (clarification.serial_number) {
                      <p class="text-sm text-base-content/60">
                        {{ clarification.article_template_description || 'Ohne Artikelvorlage' }}
                      </p>
                    }
                  </div>
                  <span class="bm-badge" [class.bm-badge-neutral]="chosenCount(clarification.id) > 0">
                    {{ chosenCount(clarification.id) }} von {{ clarification.quantity_open }} gewählt
                  </span>
                </div>

                @if (clarification.ambiguous_serial_number) {
                  <div class="bm-alert bm-alert-warning bm-alert-soft mt-2 text-xs">
                    Zu dieser Nummer gibt es mehrere aktive Einträge. Bitte selbst wählen —
                    das System entscheidet das bewusst nicht.
                  </div>
                }

                <div class="overflow-x-auto mt-3">
                  <table class="bm-table bm-table-xs">
                    <thead>
                      <tr>
                        <th class="w-10">Wählen</th>
                        <th>Artikel</th>
                        <th>Seriennummer</th>
                        <th>Zustand</th>
                        <th>Lagerplatz</th>
                        <th>Eingang</th>
                      </tr>
                    </thead>
                    <tbody>
                      @for (candidate of clarification.candidates; track candidate.id) {
                        @let chosen = isArticleSelected(clarification.id, candidate.id);
                        @let selectable = canSelect(clarification, candidate, chosen);
                        <tr
                          class="hover:bg-base-200"
                          [class.cursor-pointer]="selectable"
                          [class.opacity-50]="!selectable"
                          (click)="toggleArticle(clarification, candidate)"
                        >
                          <td>
                            <input
                              type="checkbox"
                              class="bm-checkbox bm-checkbox-sm"
                              [checked]="chosen"
                              [disabled]="!selectable"
                              (click)="$event.stopPropagation()"
                              (change)="toggleArticle(clarification, candidate)"
                            />
                          </td>
                          <td>
                            <a
                              class="text-primary underline"
                              [href]="articleUrl(candidate.id)"
                              target="_blank"
                              rel="noopener"
                              (click)="$event.stopPropagation()"
                            >
                              #{{ candidate.id }}
                            </a>
                          </td>
                          <td>{{ candidate.serial_number || '—' }}</td>
                          <td>
                            {{ locationLabel(candidate.location) }}
                            @if (candidate.bad_part) {
                              <span class="bm-badge bm-badge-xs bm-badge-error ml-1">Defektteil</span>
                            }
                            @if (candidate.blocked_reason) {
                              <span class="block text-xs text-error">
                                {{ candidate.blocked_reason }}
                                @if (candidate.transit_note) {
                                  über
                                  <a
                                    class="underline"
                                    [href]="'/a/lieferscheine/' + candidate.transit_note.id"
                                    target="_blank"
                                    rel="noopener"
                                    (click)="$event.stopPropagation()"
                                  >{{ candidate.transit_note.number || candidate.transit_note.id }}</a>
                                }
                              </span>
                            } @else if (candidate.transit_note) {
                              <span class="block text-xs text-base-content/60">
                                Unterwegs an {{ candidate.transit_note.recipient }} über
                                <a
                                  class="underline"
                                  [href]="'/a/lieferscheine/' + candidate.transit_note.id"
                                  target="_blank"
                                  rel="noopener"
                                  (click)="$event.stopPropagation()"
                                >{{ candidate.transit_note.number || candidate.transit_note.id }}</a>
                              </span>
                            }
                          </td>
                          <td>
                            {{ candidate.storage_bin_label || '—' }}
                            @if (candidate.warehouse_label) {
                              <span class="text-base-content/60">· {{ candidate.warehouse_label }}</span>
                            }
                          </td>
                          <td>
                            @if (candidate.delivery_note_id) {
                              <a
                                class="text-primary underline"
                                [href]="'/a/lieferscheine/' + candidate.delivery_note_id"
                                target="_blank"
                                rel="noopener"
                                (click)="$event.stopPropagation()"
                              >
                                {{ candidate.delivery_note_number || candidate.delivery_note_id }}
                              </a>
                              <span class="text-base-content/60">
                                {{ candidate.delivery_note_date | date: 'dd.MM.yyyy' }}
                              </span>
                            } @else {
                              <span class="text-base-content/60">über die Seriennummer</span>
                            }
                          </td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
              </div>
            </section>
          }
        }

        <!-- ==================== Schritt 3: Was bleibt übrig? ==================== -->
        @if (step() === 3 && !isRecordOnly()) {
          @if (chosenBadParts().length > 0) {
            <section class="bm-card bm-card-sm bm-card-border border-error/40">
              <div class="bm-card-body">
                <h4 class="bm-card-title text-base">Ziel-Lagerplätze</h4>
                <p class="text-sm text-base-content/70">
                  Unter den gewählten Artikeln sind <strong>{{ chosenBadParts().length }} Defektteile</strong>.
                  Die gehören nicht in den normalen Bestand und brauchen einen eigenen Lagerplatz.
                </p>

                <div class="flex flex-wrap gap-6 items-end mt-2">
                  <div>
                    <span class="bm-fieldset-label">Normale Artikel</span>
                    <p class="font-semibold">{{ receipt.storage_bin_label || '—' }}</p>
                    <p class="text-xs text-base-content/60">Lagerplatz der Buchung</p>
                  </div>

                  <div class="grow min-w-64">
                    <label class="bm-fieldset-label">Defektteile</label>
                    <select class="bm-select w-full" (change)="setBadPartBin($any($event.target).value)">
                      <option value="">Bitte wählen</option>
                      @for (bin of badPartBins().storage_bins; track bin.id) {
                        <option [value]="bin.id" [selected]="badPartBinId() === bin.id">{{ bin.label }}</option>
                      }
                    </select>
                  </div>
                </div>

                @if (needsBadPartBin()) {
                  <div class="bm-alert bm-alert-warning bm-alert-soft mt-3 text-sm">
                    <i class="fa fa-exclamation-triangle"></i>
                    <span>Bitte einen Lagerplatz für die Defektteile wählen — sonst lässt sich nicht speichern.</span>
                  </div>
                }
              </div>
            </section>
          }

          <section>
            <h4 class="font-semibold text-base mb-1">3 · Was bleibt übrig?</h4>
            <p class="text-sm text-base-content/70 mb-4">
              In Schritt 2 nachgebucht: <strong>{{ totalChosen() }}</strong>. Noch zu entscheiden:
              <strong>{{ totalRemaining() }}</strong>. Was ohne Entscheidung bleibt, bleibt offen und
              lässt sich später klären.
            </p>
          </section>

          @for (clarification of openClarifications(); track clarification.id) {
            <section class="bm-card bm-card-sm bm-card-border">
              <div class="bm-card-body">
                <div class="flex items-start justify-between gap-3">
                  <div>
                    <h5 class="bm-card-title text-base">
                      @if (clarification.serial_number) {
                        Seriennummer {{ clarification.serial_number }}
                      } @else {
                        {{ clarification.article_template_description || 'Ohne Artikelvorlage' }}
                      }
                    </h5>
                    <p class="text-sm text-base-content/60">
                      {{ clarification.quantity_open }} offen · {{ chosenCount(clarification.id) }} in
                      Schritt 2 nachgebucht
                    </p>
                  </div>
                  <div class="text-right">
                    <p class="text-sm text-base-content/60">Zu entscheiden</p>
                    <p class="text-xl font-bold" [class.text-error]="remainingFor(clarification) > 0">
                      {{ remainingFor(clarification) }}
                    </p>
                  </div>
                </div>

                @if (remainingFor(clarification) === 0) {
                  <p class="text-sm text-success mt-2">
                    Vollständig über die Auswahl in Schritt 2 gedeckt — hier ist nichts zu tun.
                  </p>
                } @else {
                  <div class="flex flex-wrap gap-4 items-end mt-3">
                    @if (clarification.serial_number) {
                      <!-- Eine Nummer, ein Gerät: Entweder-oder statt Mengen -->
                      <div class="bm-join">
                        <button
                          class="bm-btn bm-btn-sm bm-join-item"
                          [class.bm-btn-primary]="singleChoice(clarification.id) === 'ignored'"
                          (click)="toggleSingleChoice(clarification, 'ignored')"
                        >
                          Ignorieren
                        </button>
                        <button
                          class="bm-btn bm-btn-sm bm-join-item"
                          [class.bm-btn-warning]="singleChoice(clarification.id) === 'created'"
                          (click)="toggleSingleChoice(clarification, 'created')"
                        >
                          Neu anlegen
                        </button>
                      </div>
                    } @else {
                      <div>
                        <label class="bm-fieldset-label">Neu anlegen</label>
                        <input
                          type="number"
                          min="0"
                          [max]="remainingFor(clarification)"
                          class="bm-input w-24"
                          [value]="decisionFor(clarification.id).createQuantity"
                          (change)="setCreateQuantity(clarification, $any($event.target).value)"
                        />
                      </div>
                    }

                    @if (decisionFor(clarification.id).createQuantity > 0) {
                      <div>
                        <label class="bm-fieldset-label">Zustand beim Neuanlegen</label>
                        <select
                          class="bm-select w-52"
                          (change)="setCreateStatus(clarification.id, $any($event.target).value)"
                        >
                          @for (option of statusOptions; track option.value) {
                            <option
                              [value]="option.value"
                              [selected]="decisionFor(clarification.id).createStatus === option.value"
                            >
                              {{ option.label }}
                            </option>
                          }
                        </select>
                      </div>
                    }

                    @if (!clarification.serial_number) {
                      <div>
                        <label class="bm-fieldset-label">Ignorieren</label>
                        <input
                          type="number"
                          min="0"
                          [max]="remainingFor(clarification)"
                          class="bm-input w-24"
                          [value]="decisionFor(clarification.id).ignoreQuantity"
                          (change)="setIgnoreQuantity(clarification, $any($event.target).value)"
                        />
                      </div>
                    }

                    <div class="grow min-w-48">
                      <label class="bm-fieldset-label">Notiz (optional)</label>
                      <input
                        type="text"
                        class="bm-input w-full"
                        placeholder="z.B. Position war zu hoch angesetzt"
                        [value]="decisionFor(clarification.id).note"
                        (change)="setNote(clarification.id, $any($event.target).value)"
                      />
                    </div>
                  </div>

                  @if (undecidedFor(clarification) < 0) {
                    <div class="bm-alert bm-alert-error bm-alert-soft mt-3 text-sm">
                      <i class="fa fa-exclamation-triangle"></i>
                      <span>
                        Es sind nur {{ remainingFor(clarification) }} zu entscheiden — bitte die
                        Mengen anpassen.
                      </span>
                    </div>
                  } @else if (undecidedFor(clarification) > 0 && !clarification.serial_number) {
                    <p class="text-sm text-base-content/60 mt-2">
                      {{ undecidedFor(clarification) }} bleiben ohne Entscheidung und damit offen.
                    </p>
                  }
                }
              </div>
            </section>
          }
        }
      }
    </div>

    <!-- Footer -->
    <div class="flex justify-between gap-2 p-4 border-t border-base-300">
      @if (isRecordOnly()) {
        <span></span>
      } @else {
        <button class="bm-btn bm-btn-sm" [disabled]="isSaving()" (click)="close()">Abbrechen</button>
      }

      <div class="flex gap-2">
        @if (step() > 1 && !isRecordOnly()) {
          <button class="bm-btn bm-btn-sm" [disabled]="isSaving()" (click)="back()">Zurück</button>
        }
        @if (isRecordOnly()) {
          <button class="bm-btn bm-btn-sm bm-btn-primary" (click)="close()">Schließen</button>
        } @else if (step() < 3) {
          <button
            class="bm-btn bm-btn-sm bm-btn-primary"
            [disabled]="isLoading() || clarifications().length === 0"
            (click)="next()"
          >
            Weiter
          </button>
        } @else {
          <button
            class="bm-btn bm-btn-sm bm-btn-primary"
            [disabled]="isSaving() || !canSave()"
            (click)="save()"
          >
            {{ isSaving() ? 'Wird gespeichert …' : 'Alles speichern' }}
          </button>
        }
      </div>
    </div>
  </div>
</div>
